import React, { useState } from 'react';
import { Button } from './ui/button';
import { Volume2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AudioButton - Reusable component for text-to-speech functionality
 * @param {string} text - The German text to speak
 * @param {string} voice - Voice to use: "nova" (female) or "onyx" (male)
 * @param {string} size - Button size: "sm", "default", "lg", "icon"
 * @param {string} className - Additional CSS classes
 */
export function AudioButton({ text, voice = "nova", size = "sm", className = "" }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const playAudio = async () => {
    if (!text || isPlaying || isLoading) return;

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${API}/speech/synthesize`,
        { text, voice }
      );

      if (response.data.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        setIsPlaying(true);
        
        audio.onended = () => {
          setIsPlaying(false);
        };
        
        audio.onerror = () => {
          setIsPlaying(false);
          toast.error('Failed to play audio');
        };
        
        await audio.play();
      }
    } catch (error) {
      // error handled silently
      toast.error('Failed to generate audio');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      size={size}
      variant="ghost"
      onClick={playAudio}
      disabled={isLoading || !text}
      className={`text-teal-500 hover:text-teal-400 hover:bg-teal-500/10 p-1 ${className}`}
      title="Play audio"
      data-testid="audio-play-btn"
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <Volume2 className={`h-4 w-4 ${isPlaying ? 'animate-pulse text-yellow-400' : ''}`} />
      )}
    </Button>
  );
}

/**
 * AudioText - Text with integrated audio button
 * @param {string} text - The German text to display and speak
 * @param {string} translation - Optional translation to display below
 * @param {string} voice - Voice to use
 * @param {string} className - Additional CSS classes for the text
 */
export function AudioText({ text, translation, voice = "nova", className = "", textClassName = "" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <span className={textClassName}>{text}</span>
      <AudioButton text={text} voice={voice} />
      {translation && (
        <span className="text-muted-foreground text-sm">({translation})</span>
      )}
    </div>
  );
}

export default AudioButton;
